import { useState } from "react";
import { 
  ChevronDown, 
  ChevronRight, 
  Calculator, 
  Trash2, 
  Target, 
  Gauge,
  Database,
  FileText,
} from "lucide-react";

const IncineratorOverview = () => {
  const [openSections, setOpenSections] = useState(false);

  const toggleSection = () => {
    setOpenSections((prev) => !prev);
  };

  const dataSources = [
    {
      collection: "VesselIncineratorData",
      fields: [
        "vesselId", "voyageId", "date", "sludgeOil", "bilgeWaterResidues", 
        "solidGarbage", "foodWaste", "incineratorConsMTHFO", "incineratorConsMTLFO"
      ],
      purpose: "Incinerator waste processing data and fuel consumption"
    },
  ];

  const coreCalculations = [
    {
      id: "target-values-definition",
      title: "Target Values Definition",
      description: "Standard processing targets for different waste types",
      formula: "Targets = Industry standards based on waste type and regulations",
      subFormulas: [
        "sludgeOil: 10 kg/h (MARPOL Annex I)",
        "bilgeWaterResidues: 7 kg/h (MARPOL Annex I)",
        "solidGarbage: 6 kg/h (MARPOL Annex V)",
        "foodWaste: 4 kg/h (MARPOL Annex V)",
        "Based on incinerator design and regulatory limits"
      ],
      icon: <Target className="w-5 h-5" />,
      borderColor: "border-purple-200",
      bgColor: "bg-purple-50",
      textColor: "text-purple-700",
    },
    {
      id: "metric-status-calculation",
      title: "Metric Status Calculation",
      description: "Determines performance status based on target ranges",
      formula: "getMetricStatus(value, target) → High/Low/Optimal",
      subFormulas: [
        "Tolerance: 10% of target value",
        "Upper bound: target × 1.1",
        "Lower bound: target × 0.9",
        "High: value > upper bound",
        "Low: value < lower bound",
        "Optimal: within bounds"
      ],
      icon: <Gauge className="w-5 h-5" />,
      borderColor: "border-amber-200",
      bgColor: "bg-amber-50",
      textColor: "text-amber-700",
    },
    {
      id: "optimal-range-calculation",
      title: "Optimal Range Calculation",
      description: "Calculates acceptable performance ranges with tolerance",
      formula: "calculateOptimalRange(target) → [lowerBound, upperBound]",
      subFormulas: [
        "Default tolerance: 10% (0.1)",
        "Lower bound = target × (1 - tolerance)",
        "Upper bound = target × (1 + tolerance)",
        "Example: target=10 → range=[9, 11]",
        "Fixed tolerance for consistency"
      ],
      icon: <Calculator className="w-5 h-5" />,
      borderColor: "border-red-200",
      bgColor: "bg-red-50",
      textColor: "text-red-700",
    }
  ];

  const wasteTypes = [
    {
      type: "Sludge Oil",
      field: "sludgeOil",
      target: "10 kg/h",
      regulatory: "MARPOL Annex I",
      description: "Oil residues from fuel and lubricating oil purification",
      composition: "Heavy hydrocarbons, water, solid particles"
    },
    {
      type: "Bilge Water Residues",
      field: "bilgeWaterResidues",
      target: "7 kg/h",
      regulatory: "MARPOL Annex I",
      description: "Residuals from bilge water treatment systems",
      composition: "Oil-water mixture with contaminants"
    },
    {
      type: "Solid Garbage",
      field: "solidGarbage",
      target: "6 kg/h",
      regulatory: "MARPOL Annex V",
      description: "Non-food solid waste from vessel operations",
      composition: "Plastics, packaging, operational waste"
    },
    {
      type: "Food Waste",
      field: "foodWaste",
      target: "4 kg/h",
      regulatory: "MARPOL Annex V",
      description: "Organic waste from galley and food services",
      composition: "Biodegradable organic matter"
    }
  ];

  return (
    <div>
      <div className="mb-6 border border-gray-300 rounded-lg overflow-hidden">
        <button
          className="flex items-center justify-between w-full p-5 bg-gray-50 hover:bg-gray-100 transition-colors"
          onClick={toggleSection}
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-[#fce3d2] rounded-lg">
              <Trash2 className="w-6 h-6 text-[#d35400]" />
            </div>
            <div className="text-left">
              <h2 className="text-md font-bold text-gray-800">
                Incinerator Performance Calculations
              </h2>
              <p className="text-gray-600 text-sm">
                Waste processing analysis, efficiency metrics, and regulatory compliance tracking
              </p>
            </div>
          </div>
          {openSections ? (
            <ChevronDown className="w-5 h-5 text-gray-500" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-500" />
          )}
        </button>

        {openSections && (
          <div className="p-5 bg-white border-t border-gray-200 space-y-8">
            {/* Data Sources */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Database className="w-5 h-5 text-gray-600" />
                <h3 className="text-lg font-semibold text-gray-800">Data Sources & Collections</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
                {dataSources.map((source, index) => (
                  <div key={index} className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <div className="flex items-center gap-2 mb-2">
                      <FileText className="w-4 h-4 text-gray-600" />
                      <h4 className="font-semibold text-gray-800">{source.collection}</h4>
                    </div>
                    <p className="text-sm text-gray-700 mb-3">{source.purpose}</p>
                    <div className="text-xs text-gray-600 space-y-1">
                      <div className="font-medium mb-1">Key Fields:</div>
                      {source.fields.map((field, idx) => (
                        <div key={idx} className="font-mono truncate">• {field}</div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Core Calculations */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Core Calculations & Logic</h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                {coreCalculations.map((calc) => (
                  <div
                    key={calc.id}
                    className={`p-5 rounded-lg border ${calc.borderColor} ${calc.bgColor}`}
                  >
                    <div className="flex items-start gap-3 mb-4">
                      <div className={`p-2 rounded-lg ${calc.textColor} bg-white`}>
                        {calc.icon}
                      </div>
                      <div className="flex-1">
                        <h4 className={`font-bold text-gray-800 mb-1 ${calc.textColor}`}>
                          {calc.title}
                        </h4>
                        <p className="text-sm text-gray-600">{calc.description}</p>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div>
                        <p className="text-sm font-medium text-gray-700 mb-1">Logic:</p>
                        <div className="text-sm bg-white p-3 rounded border border-gray-200 font-mono">
                          {calc.formula}
                        </div>
                      </div>
                      
                      {calc.subFormulas && (
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-2">Implementation:</p>
                          <div className="space-y-2">
                            {calc.subFormulas.map((subFormula, idx) => (
                              <div key={idx} className="text-sm bg-white p-2 rounded border border-gray-100">
                                {subFormula}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Waste Types */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Waste Type Specifications</h3>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {wasteTypes.map((waste, index) => (
                    <div key={index} className="bg-white p-4 rounded border">
                      <div className="font-semibold text-gray-800 mb-2">{waste.type}</div>
                      <div className="text-xs text-gray-600 mb-1">Database Field: <span className="font-mono">{waste.field}</span></div>
                      <div className="text-lg font-bold text-green-700 mb-2">Target: {waste.target}</div>
                      <div className="text-xs text-gray-600 mb-1">Regulatory: {waste.regulatory}</div>
                      <p className="text-sm text-gray-700 mb-2">{waste.description}</p>
                      <div className="text-xs text-gray-500">Composition: {waste.composition}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default IncineratorOverview;